import { useState, useCallback, useEffect, useRef } from 'react';
import { maskPhone } from '../utils/masks';

/**
 * Hook para gerenciamento de múltiplos telefones
 * @param {Array<string>} initialPhones - Array inicial de telefones
 * @returns {Object} Objeto com phoneNumbers, handlePhoneChange, addPhone, removePhone, setPhoneNumbers
 */
export function usePhoneNumbers(initialPhones = ['']) {
    const [phoneNumbers, setPhoneNumbers] = useState(() => initialPhones.map(phone => maskPhone(phone || '')));
    const initialPhonesRef = useRef(initialPhones);
    const isInitialMount = useRef(true);
    
    // Atualiza o estado apenas quando initialPhones muda externamente (não na primeira renderização)
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            initialPhonesRef.current = initialPhones;
            return;
        }
        
        // Compara usando referência para evitar loops
        if (initialPhonesRef.current === initialPhones) {
            return;
        }
        
        // Só atualiza se realmente mudou
        const maskedInitial = initialPhones.map(phone => maskPhone(phone || ''));
        const hasChanged = phoneNumbers.length !== maskedInitial.length ||
            phoneNumbers.some((phone, i) => phone !== maskedInitial[i]);
        
        if (hasChanged) {
            initialPhonesRef.current = initialPhones;
            setPhoneNumbers(maskedInitial);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- incluir phoneNumbers reexecutaria ao digitar e geraria loop
    }, [initialPhones]);

    /**
     * Handler para mudança em um telefone específico
     * @param {number} index - Índice do telefone no array
     * @param {string} value - Novo valor (será mascarado automaticamente)
     */
    const handlePhoneChange = useCallback((index, value) => {
        const maskedValue = maskPhone(value);
        setPhoneNumbers(prev => {
            const newPhones = [...prev];
            newPhones[index] = maskedValue;
            return newPhones;
        });
    }, []);

    /**
     * Adiciona um novo campo de telefone
     */
    const addPhone = useCallback(() => {
        setPhoneNumbers(prev => [...prev, '']);
    }, []);

    /**
     * Remove um telefone do array
     * @param {number} index - Índice do telefone a remover
     */
    const removePhone = useCallback((index) => {
        setPhoneNumbers(prev => prev.filter((_, i) => i !== index));
    }, []);

    /**
     * Define todos os telefones de uma vez
     * @param {Array<string>} phones - Array de telefones
     */
    const setPhones = useCallback((phones) => {
        if (!Array.isArray(phones)) {
            return;
        }
        const maskedPhones = phones.map(phone => maskPhone(phone || ''));
        setPhoneNumbers(prev => {
            // Só atualiza se realmente mudou para evitar loops
            const hasChanged = prev.length !== maskedPhones.length || 
                prev.some((p, i) => p !== maskedPhones[i]);
            return hasChanged ? maskedPhones : prev;
        });
    }, []);

    /**
     * Reseta para array com um telefone vazio
     */
    const resetPhones = useCallback(() => {
        setPhoneNumbers(['']);
    }, []);

    /**
     * Retorna apenas telefones não vazios
     * @returns {Array<string>} Array de telefones válidos
     */
    const getValidPhones = useCallback(() => {
        return phoneNumbers.filter(phone => phone.trim().length > 0);
    }, [phoneNumbers]);

    /**
     * Formata telefones para envio à API
     * @returns {Array<Object>} Array de objetos { number: string }
     */
    const formatForApi = useCallback(() => {
        return getValidPhones().map(phone => ({ number: phone }));
    }, [getValidPhones]);

    return {
        phoneNumbers,
        setPhoneNumbers: setPhones,
        handlePhoneChange,
        addPhone,
        removePhone,
        resetPhones,
        getValidPhones,
        formatForApi,
    };
}
